import React, { useState, useMemo, useEffect } from "react";
import { Box, TextField, Button, Stack, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Typography, Pagination, InputAdornment, Avatar, Divider, Autocomplete, Checkbox } from "@mui/material";
import { Plus, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { identityService } from "../../api/services/identityService";
import { projectService } from "../../api/services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader } from "../../components/Common";
import DataTable from "../../components/DataTable";
import { avatarColor, initials } from "../../utils/avatarColor";

// Client-side paging: HRMS's /employees/ returns the whole list in one
// response (no server-side paging available), so we fetch once and page
// through it in memory. Fine at ~600 employees; if a tenant ever gets to
// several thousand, this should move to a server-paged endpoint rather
// than growing the payload.
const PAGE_SIZE = 25;

const emptyForm = {
  name: "", email: "",
  manager_id: "", branch_location: "", date_of_joining: "",
  pan_number: "", aadhar_number: "", address: "",
};

// Compact, HRMS-table-style select: no floating label (the column header
// already says what it is), smaller box/text.
const compactSelectSx = {
  minWidth: 130,
  "& .MuiInputBase-root": { fontSize: 13 },
  "& .MuiSelect-select": { py: 0.5 },
};

export default function EmployeesPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // Companies that already run HRMS manage their employee roster there --
  // ATS just consumes it read-only. Only companies without HRMS (ATS
  // requested it directly) are allowed to create employees from here.
  const { me } = useAuth();
  const canCreateEmployees = !me?.hrms_enabled;
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [manager, setManager] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: employees, isLoading } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: identityService.listRoles });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams });
  const { data: userRoles } = useQuery({ queryKey: ["user-roles"], queryFn: identityService.listUserRoles });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: projectService.list });

  // employee_id -> current role/team/projects, so the inline dropdowns start
  // pre-filled with whatever's already assigned instead of blank.
  const roleByEmployeeId = useMemo(
    () => Object.fromEntries((userRoles || []).filter((r) => r.user_type === "employee" && r.employee_id).map((r) => [r.employee_id, r])),
    [userRoles]
  );
  const teamByEmployeeId = useMemo(() => {
    const map = {};
    for (const t of teams || []) {
      for (const empId of t.member_employee_ids) map[empId] = t.id;
    }
    return map;
  }, [teams]);
  // employee_id -> array of projects -- unlike role/team, an employee can be
  // on multiple projects at once.
  const projectsByEmployeeId = useMemo(() => {
    const map = {};
    for (const p of projects || []) {
      for (const empId of p.member_employee_ids || []) {
        if (!map[empId]) map[empId] = [];
        map[empId].push(p);
      }
    }
    return map;
  }, [projects]);

  const filtered = useMemo(() => {
    const list = employees || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((e) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q) ||
      (e.employee_code || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // If a search narrows the list while you're on, say, page 7, that page no
  // longer exists -- snap back rather than rendering an empty table.
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const createMutation = useMutation({
    // Two calls, on purpose: the employee record itself is created via the
    // ATS-owned endpoint (see identityService.createEmployee), then
    // PAN/Aadhaar/Address are saved separately into the ATS employee
    // profile table -- ATS keeps its own copy of this data rather than
    // writing it onto the shared HRMS record.
    mutationFn: async () => {
      const created = await identityService.createEmployee({
        name: form.name, email: form.email,
        manager_id: manager ? manager.id : null,
        branch_location: form.branch_location || null,
        date_of_joining: form.date_of_joining || null,
      });
      if (form.pan_number || form.aadhar_number || form.address) {
        await identityService.saveEmployeeProfile(created.id, {
          pan_number: form.pan_number || null,
          aadhar_number: form.aadhar_number || null,
          address: form.address || null,
        });
      }
      return created;
    },
    onSuccess: () => {
      setCreateOpen(false);
      setForm(emptyForm);
      setManager(null);
      enqueueSnackbar("Employee created — an account setup email has been sent", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to create employee", { variant: "error" }),
  });

  // Inline, always-editable -- no popup, no separate "save" step. Picking a
  // value in the table row saves immediately.
  const roleMutation = useMutation({
    mutationFn: ({ employeeId, roleId }) =>
      roleId
        ? identityService.assignRole({ user_type: "employee", employee_id: employeeId, role_id: Number(roleId) })
        : identityService.revokeRole("employee", employeeId),
    onSuccess: () => {
      enqueueSnackbar("Role updated", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["user-roles"] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to update role", { variant: "error" }),
  });

  const teamMutation = useMutation({
    // Single-team model: dropping to a new team removes membership from
    // whichever team they were already in first, so the dropdown always
    // reflects exactly one selection.
    mutationFn: async ({ employeeId, fromTeamId, toTeamId }) => {
      if (fromTeamId && fromTeamId !== toTeamId) await identityService.removeTeamMember(fromTeamId, employeeId);
      if (toTeamId) await identityService.addTeamMember(toTeamId, employeeId);
    },
    onSuccess: () => {
      enqueueSnackbar("Team updated", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to update team", { variant: "error" }),
  });

  const projectMutation = useMutation({
    // Multi-project model: diff the previous vs. newly-selected set and
    // add/remove only what changed.
    mutationFn: async ({ employeeId, previousIds, nextIds }) => {
      const toAdd = nextIds.filter((id) => !previousIds.includes(id));
      const toRemove = previousIds.filter((id) => !nextIds.includes(id));
      await Promise.all([
        ...toAdd.map((projectId) => projectService.addMember(projectId, employeeId)),
        ...toRemove.map((projectId) => projectService.removeMember(projectId, employeeId)),
      ]);
    },
    onSuccess: () => {
      enqueueSnackbar("Projects updated", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to update projects", { variant: "error" }),
  });

  const columns = [
    { key: "name", label: "Name", render: (e) => <strong>{e.name}</strong> },
    { key: "employee_code", label: "Code", render: (e) => e.employee_code || "—" },
    { key: "email", label: "Email" },
    { key: "department_name", label: "Department", render: (e) => e.department_name || "—" },
    { key: "is_active", label: "Status", render: (e) => <Chip size="small" label={e.is_active ? "Active" : "Inactive"} /> },
    {
      key: "role", label: "Role", width: 140,
      render: (e) => (
        <TextField
          select size="small" value={roleByEmployeeId[e.id]?.role_id || ""}
          onClick={(ev) => ev.stopPropagation()}
          onChange={(ev) => roleMutation.mutate({ employeeId: e.id, roleId: ev.target.value })}
          sx={compactSelectSx}
        >
          <MenuItem value="">— No role —</MenuItem>
          {(roles || []).map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
      ),
    },
    {
      key: "team", label: "Team", width: 140,
      render: (e) => (
        <TextField
          select size="small" value={teamByEmployeeId[e.id] || ""}
          onClick={(ev) => ev.stopPropagation()}
          onChange={(ev) => teamMutation.mutate({ employeeId: e.id, fromTeamId: teamByEmployeeId[e.id] || null, toTeamId: ev.target.value || null })}
          sx={compactSelectSx}
        >
          <MenuItem value="">— No team —</MenuItem>
          {(teams || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
      ),
    },
    {
      key: "projects", label: "Project", width: 160,
      render: (e) => {
        const currentIds = (projectsByEmployeeId[e.id] || []).map((p) => p.id);
        return (
          <TextField
            select size="small" value={currentIds}
            onClick={(ev) => ev.stopPropagation()}
            onChange={(ev) => projectMutation.mutate({ employeeId: e.id, previousIds: currentIds, nextIds: ev.target.value })}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length === 0 ? "—" : `${selected.length} project${selected.length > 1 ? "s" : ""}`),
            }}
            sx={compactSelectSx}
          >
            {(projects || []).length === 0 && <MenuItem disabled value="">No projects yet</MenuItem>}
            {(projects || []).map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Checkbox size="small" checked={currentIds.includes(p.id)} sx={{ p: 0.5, mr: 0.5 }} />
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
  ];

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <Box>
      <PageHeader
        title="Employees"
        subtitle="Assign ATS roles and teams inline from the table. Once assigned a role, an employee can log in and see tickets scoped to that role."
        action={canCreateEmployees ? (
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>New Employee</Button>
        ) : null}
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search name, email, or employee code..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {filtered.length === 0
            ? "No matches"
            : `Showing ${rangeStart}–${rangeEnd} of ${filtered.length}${
                employees && filtered.length !== employees.length ? ` (filtered from ${employees.length})` : ""
              }`}
        </Typography>
      </Stack>

      <DataTable
        columns={columns}
        rows={pageRows}
        loading={isLoading}
        emptyMessage={search ? "No employees match that search" : "No employees yet"}
        onRowClick={(e) => setDetailTarget(e)}
        footer={
          totalPages > 1 && (
            <Stack direction="row" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Stack>
          )
        }
      />

      <Dialog open={canCreateEmployees && createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Employee</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField
              label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth required helperText="They'll get an email to set up their own password, same as HRMS onboarding"
            />
            <Autocomplete
              size="small"
              options={employees || []}
              getOptionLabel={(e) => e.name || e.email}
              value={manager}
              onChange={(_, v) => setManager(v)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Manager" placeholder="Search employees..." />}
            />
            <TextField label="Branch" value={form.branch_location} onChange={(e) => setForm({ ...form, branch_location: e.target.value })} fullWidth />
            <TextField
              label="Date of Joining" type="date" InputLabelProps={{ shrink: true }} fullWidth
              value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })}
            />
            <Divider />
            <TextField label="PAN Number" value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value })} fullWidth />
            <TextField label="Aadhaar Number" value={form.aadhar_number} onChange={(e) => setForm({ ...form, aadhar_number: e.target.value })} fullWidth />
            <TextField label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!form.name || !form.email || createMutation.isPending} onClick={() => createMutation.mutate()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <EmployeeDetailDialog
        employee={detailTarget}
        roleName={detailTarget ? roleByEmployeeId[detailTarget.id]?.role_name : null}
        teamName={detailTarget ? (teams || []).find((t) => t.id === teamByEmployeeId[detailTarget.id])?.name : null}
        projectNames={detailTarget ? (projectsByEmployeeId[detailTarget.id] || []).map((p) => p.name).join(", ") : null}
        onClose={() => setDetailTarget(null)}
      />
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>{value || "—"}</Typography>
    </Box>
  );
}

// Hirality's employee record has many HR-only sections (salary, bank
// details, PF/tax settings, etc.) that the API happens to return alongside
// everything else -- this deliberately shows a curated subset (identity +
// contact info, ATS role/team, plus the PAN/Aadhaar/Address ATS itself
// captures) rather than dumping the full HRMS payroll profile.
function EmployeeDetailDialog({ employee, roleName, teamName, projectNames, onClose }) {
  if (!employee) return null;

  return (
    <Dialog open={Boolean(employee)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 700 }}>
        <Avatar sx={{ bgcolor: avatarColor(employee.name), width: 44, height: 44 }}>{initials(employee.name)}</Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{employee.name}</Typography>
          <Typography variant="caption" color="text.secondary">{employee.employee_code || "No code"}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Chip size="small" label={employee.is_active ? "Active" : "Inactive"} color={employee.is_active ? "success" : "default"} />
          {employee.employment_type && <Chip size="small" label={employee.employment_type} />}
        </Stack>
        <Divider />
        <Box sx={{ mt: 0.5 }}>
          <DetailRow label="Email" value={employee.email} />
          <DetailRow label="Phone" value={employee.phone} />
          <DetailRow label="Department" value={employee.department_name} />
          <DetailRow label="Designation" value={employee.designation_title} />
          <DetailRow label="Manager" value={employee.manager_name} />
          <DetailRow label="Branch" value={employee.branch_location} />
          <DetailRow
            label="Date of Joining"
            value={employee.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString() : null}
          />
          <DetailRow label="Role" value={roleName} />
          <DetailRow label="Team" value={teamName} />
          <DetailRow label="Projects" value={projectNames} />
          <DetailRow label="PAN Number" value={employee.pan_number} />
          <DetailRow label="Aadhaar Number" value={employee.aadhar_number} />
          <DetailRow label="Address" value={employee.address} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
